import {DependabotCheckIcon} from '@github-ui/pacer/CustomIcon'
import {DependabotIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Spinner} from '@primer/react'
import {useMemo} from 'react'

import {useCustomAgentsQuery} from '../../hooks/use-custom-agents-query'
import {IconButtonSelector} from './IconButtonSelector'

type CustomAgentSelectorProps = {
  nwo: string | undefined
  branch: string | undefined
  selectedAgent: string | undefined
  onSelect: (agent: string | undefined) => void
}

export function CustomAgentSelector({nwo, branch, selectedAgent, onSelect}: CustomAgentSelectorProps) {
  const {data: agents, isLoading} = useCustomAgentsQuery(nwo)

  const selectedAgentObj = agents?.find(agent => agent.name === selectedAgent)
  const selectedAgentName = selectedAgentObj?.display_name || selectedAgentObj?.name

  const newFileUrl = useMemo(() => {
    const params = new URLSearchParams({
      filename: '.github/agents/my-agent.md',
      value: '---\nname:\ndescription:\n---\n\n# My Agent\n\nDescribe what your agent does here...',
    })
    return `/${nwo}/new/${branch}?${params.toString()}`
  }, [nwo, branch])

  const ariaLabel = selectedAgentName ? `Custom agent: ${selectedAgentName}` : 'Select a custom agent'

  const disabled = !nwo || !branch

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButtonSelector
          icon={DependabotIcon}
          selectedIcon={DependabotCheckIcon}
          aria-label={ariaLabel}
          selected={Boolean(selectedAgent)}
          disabled={disabled}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        <ActionList>
          {isLoading ? (
            <>
              <ActionList.Item>
                <ActionList.LeadingVisual>
                  <Spinner size="small" />
                </ActionList.LeadingVisual>
                Loading
              </ActionList.Item>
              <ActionList.Divider />
            </>
          ) : (
            agents &&
            agents.length > 0 && (
              <>
                <ActionList.Group selectionVariant="single">
                  <ActionList.Item selected={selectedAgent === null} onSelect={() => onSelect(undefined)}>
                    None
                  </ActionList.Item>
                  {agents.map(agent => (
                    <ActionList.Item
                      key={agent.name}
                      selected={selectedAgent === agent.name}
                      onSelect={() => onSelect(agent.name)}
                    >
                      {agent.display_name || agent.name}
                    </ActionList.Item>
                  ))}
                </ActionList.Group>
                <ActionList.Divider />
              </>
            )
          )}

          <ActionList.Group selectionVariant={false}>
            <ActionList.LinkItem href={newFileUrl} target="_blank">
              <ActionList.LeadingVisual>
                <PlusIcon />
              </ActionList.LeadingVisual>
              Create an agent
            </ActionList.LinkItem>
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ CustomAgentSelector.displayName ||= 'CustomAgentSelector' } catch {}